import { animate, style, transition, trigger } from '@angular/animations';
import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { Category } from '~app/modules/generic/models';
import { GridHeightUtil } from '../../utils/grid-height.util';
import { GtmDatalayerService } from '~shared/services/gtm-datalayer.service';
import { GATrackingEventType, GaTrackingEventAction, GaTrackingEventName } from '~shared/modules/ga-tracking/constants';

@Component({
  selector: 'app-sub-category-grid',
  templateUrl: './sub-category-grid.component.html',
  styleUrls: ['./sub-category-grid.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('fadeIn', [
      transition(':enter', [style({ opacity: '0' }), animate('.3s ease-in', style({ opacity: '1' }))]),
      transition(':leave', [style({ opacity: '1' }), animate('.3s ease-out', style({ opacity: '0' }))]),
    ]),
  ],
})
export class SubCategoryGridComponent {
  @Input() categories: Category[];
  @Input() parentCategoryName: string;
  @Input() isMobile: boolean;
  @Input() isTablet: boolean;
  @Input() isDesktop: boolean;

  readonly MAX_MOBILE_CATEGORIES = 6;
  readonly MAX_TABLET_AND_DESKTOP_CATEGORIES = 10;
  readonly MOBILE_CARD_HEIGHT = 114;
  readonly MOBILE_CARD_GAP = 8;
  readonly MOBILE_CARDS_PER_ROW = 3;
  readonly TABLET_CARD_HEIGHT = 162;
  readonly DESKTOP_CARD_HEIGHT = 86;
  readonly TABLET_AND_DESKTOP_CARD_GAP = 16;
  readonly TABLET_AND_DESKTOP_CARDS_PER_ROW = 5;

  constructor(private gtmDatalayerService: GtmDatalayerService) {}

  showAllCategories = false;

  get limit(): number {
    return this.showAllCategories
      ? this.categories.length
      : this.isMobile
      ? this.MAX_MOBILE_CATEGORIES
      : this.MAX_TABLET_AND_DESKTOP_CATEGORIES;
  }

  get showAllCategoriesButton(): boolean {
    return (
      (this.isMobile && this.categories.length > this.MAX_MOBILE_CATEGORIES) ||
      (!this.isMobile && this.categories.length > this.MAX_TABLET_AND_DESKTOP_CATEGORIES)
    );
  }

  get maxHeightMobile(): number {
    return GridHeightUtil.getGridMaxHeight(
      this.categories.length,
      this.MOBILE_CARDS_PER_ROW,
      this.MOBILE_CARD_GAP,
      this.MOBILE_CARD_HEIGHT
    );
  }

  get maxHeightTablet(): number {
    return GridHeightUtil.getGridMaxHeight(
      this.categories.length,
      this.TABLET_AND_DESKTOP_CARDS_PER_ROW,
      this.TABLET_AND_DESKTOP_CARD_GAP,
      this.TABLET_CARD_HEIGHT
    );
  }

  get maxHeightDesktop(): number {
    return GridHeightUtil.getGridMaxHeight(
      this.categories.length,
      this.TABLET_AND_DESKTOP_CARDS_PER_ROW,
      this.TABLET_AND_DESKTOP_CARD_GAP,
      this.DESKTOP_CARD_HEIGHT
    );
  }

  get minHeightMobile(): number {
    return GridHeightUtil.getGridMinHeight(this.MOBILE_CARD_HEIGHT, this.MOBILE_CARD_GAP);
  }

  get minHeightTablet(): number {
    return GridHeightUtil.getGridMinHeight(this.TABLET_CARD_HEIGHT, this.TABLET_AND_DESKTOP_CARD_GAP);
  }

  get minHeightDesktop(): number {
    return GridHeightUtil.getGridMinHeight(this.DESKTOP_CARD_HEIGHT, this.TABLET_AND_DESKTOP_CARD_GAP);
  }

  initializeCategoryGridView(): void {
    this.gtmDatalayerService.gtagEvent({
      event: GATrackingEventType.SE_PLP_CATEGORY_NAVIGATION_VIEWED,
      GA_eventname: GaTrackingEventName.GridNavigation,
      action: GaTrackingEventAction.PLP_CATEGORY_NAVIGATION_VIEWED,
    });
  }

  onShowAllClick(): void {
    if (this.showAllCategories) {
      this.showLessCategoriesEvent();
    } else {
      this.showAllCategoriesEvent();
    }
    this.showAllCategories = !this.showAllCategories;
  }

  private showAllCategoriesEvent(): void {
    this.gtmDatalayerService.gtagEvent({
      event: GATrackingEventType.SE_PLP_CATEGORY_NAVIGATION_EXPANDED,
      GA_eventname: GaTrackingEventName.GridNavigation,
      action: GaTrackingEventAction.PLP_CATEGORY_NAVIGATION_EXPANDED,
      category: this.parentCategoryName,
      label: 'click to show all',
    });
  }

  private showLessCategoriesEvent(): void {
    this.gtmDatalayerService.gtagEvent({
      event: GATrackingEventType.SE_PLP_CATEGORY_NAVIGATION_COLLAPSED,
      GA_eventname: GaTrackingEventName.GridNavigation,
      action: GaTrackingEventAction.PLP_CATEGORY_NAVIGATION_COLLAPSED,
      category: this.parentCategoryName,
      label: 'show less',
    });
  }
}
